import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Button,
} from "@mui/material";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";

function mobileQueueRouteFor(sublocationId) {
  if (sublocationId == null || sublocationId === "") return null;
  return `/${sublocationId}/mobilequeuescreen`;
}

function ScreenCard({ label, hierarchyLabel, onClick }) {
  return (
    <Card
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        transition: "0.25s ease",
        textAlign: "center",
        "&:hover": {
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          transform: "translateY(-4px)",
        },
      }}
    >
      <CardActionArea
        onClick={onClick}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            px: 2,
            minHeight: 200,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              background:
                "radial-gradient(circle at 30% 20%, #ecfccb, #d9f99d)",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2.5,
            }}
          >
            <SmartphoneIcon sx={{ fontSize: 32, color: "#4d7c0f" }} />
          </Box>

          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ px: 1, textAlign: "center", wordBreak: "break-word" }}
          >
            {label}
          </Typography>

          <Box
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: "#92400e",
              backgroundColor: "rgba(146,64,14,0.12)",
            }}
          >
              Sublocation
            </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function ScreenRow({ items, renderItem, keyField = "key" }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
      }}
    >
      {items.map((item) => {
        const key = item[keyField] ?? item.key;
        return (
          <Box
            key={key}
            sx={{
              width: 220,
              flex: "0 0 220px",
            }}
          >
            {renderItem(item)}
          </Box>
        );
      })}
    </Box>
  );
}

function getScreenScopes(roles = []) {
  const scopes = {
    all: false,
    siteIds: new Set(),
    locationIds: new Set(),
    sublocationIds: new Set(),
  };

  for (const raw of roles) {
    const role = String(raw || "").trim();

    if (role === "superadmin" || role === "screens:all") scopes.all = true;

    const siteMatch = role.match(/^screens:site:(\d+)$/);
    if (siteMatch) scopes.siteIds.add(Number(siteMatch[1]));

    const locMatch = role.match(/^screens:location:(\d+)$/);
    if (locMatch) scopes.locationIds.add(Number(locMatch[1]));

    const subMatch = role.match(/^screens:sublocation:(\d+)$/);
    if (subMatch) scopes.sublocationIds.add(Number(subMatch[1]));
  }

  return scopes;
}

function getLoggedInDisplay(user = {}) {
  return (
    user?.name ||
    user?.preferred_username ||
    user?.email ||
    user?.given_name ||
    "Unknown User"
  );
}

export default function MobileQueueSelection() {
  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const loggedInAs = getLoggedInDisplay(auth.user);
  const userHandle =
    auth.user?.preferred_username || auth.user?.email || auth.user?.sub || "";

  const handleLogout = async () => {
    try {
      if (window._socket?.connected) window._socket.disconnect();
      await logout({ redirectUri: window.location.origin });
    } catch (e) {
      console.error("Logout failed:", e);
    }
  };

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const res = await apiFetch("/updates-reports/stations");
        const data = await res.json();
        if (!active) return;

        const mapped = data.map((s) => ({
          key:
            s.sublocation_id != null
              ? `sub:${s.sublocation_id}`
              : `sub:unassigned:${s.id}`,
          siteId: s.site_id ?? null,
          locationId: s.location_id ?? null,
          sublocationId: s.sublocation_id ?? null,
          siteName: s.site_name || "Unassigned Site",
          locationName: s.location_name || "Unassigned Location",
          sublocationName: s.sublocation_name || "Unassigned Sublocation",
        }));

        setRows(mapped);
      } catch (err) {
        console.error("Failed to fetch mobile queue destinations:", err);
        if (active) setRows([]);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  const scopes = useMemo(() => getScreenScopes(auth.roles || []), [auth.roles]);

  const visibleSublocations = useMemo(() => {
    const filtered = rows.filter((row) => {
      if (scopes.all) return true;
      const siteOk = row.siteId != null && scopes.siteIds.has(Number(row.siteId));
      const locOk =
        row.locationId != null && scopes.locationIds.has(Number(row.locationId));
      const subOk =
        row.sublocationId != null &&
        scopes.sublocationIds.has(Number(row.sublocationId));
      return siteOk || locOk || subOk;
    });

    const deduped = new Map();
    for (const row of filtered) {
      if (row.sublocationId == null) continue;
      if (!deduped.has(row.key)) deduped.set(row.key, row);
    }

    return Array.from(deduped.values()).sort((a, b) => {
      const siteCompare = String(a.siteName || "").localeCompare(String(b.siteName || ""));
      if (siteCompare !== 0) return siteCompare;
      const locCompare = String(a.locationName || "").localeCompare(String(b.locationName || ""));
      if (locCompare !== 0) return locCompare;
      return String(a.sublocationName || "").localeCompare(String(b.sublocationName || ""));
    });
  }, [rows, scopes]);

  const groupedSublocations = useMemo(() => {
    const result = {};

    visibleSublocations.forEach((row) => {
      const siteIdKey = row.siteId != null ? `site:${row.siteId}` : "site:unassigned";
      const locIdKey =
        row.locationId != null ? `loc:${row.locationId}` : "loc:unassigned";

      if (!result[siteIdKey]) {
        result[siteIdKey] = {
          siteId: row.siteId ?? null,
          siteName: row.siteName || "Unassigned Site",
          locations: {},
        };
      }

      if (!result[siteIdKey].locations[locIdKey]) {
        result[siteIdKey].locations[locIdKey] = {
          locationId: row.locationId ?? null,
          locationName: row.locationName || "Unassigned Location",
          items: [],
        };
      }

      result[siteIdKey].locations[locIdKey].items.push(row);
    });

    return result;
  }, [visibleSublocations]);

  const hasScopeRoles = useMemo(() => {
    if (scopes.all) return true;
    return (
      scopes.siteIds.size > 0 ||
      scopes.locationIds.size > 0 ||
      scopes.sublocationIds.size > 0
    );
  }, [scopes]);

  return (
    <Box
      sx={{
        width: "100%",
        py: 6,
        px: 3,
        display: "flex",
        justifyContent: "center",
        background: "linear-gradient(to bottom right, #f7fee7, #ffffff)",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "1400px" }}>
        <Box
          sx={{
            position: "relative",
            mb: 6,
            minHeight: { xs: "auto", md: 180 },
            pr: { xs: 0, md: 42 },
          }}
        >
          <Card
            sx={{
              position: { xs: "static", md: "absolute" },
              top: -8,
              right: 0,
              width: { xs: "100%", md: 300 },
              mb: { xs: 3, md: 0 },
              borderRadius: 3,
              textAlign: "left",
              background:
                "linear-gradient(135deg, rgba(77,124,15,0.08), rgba(255,255,255,0.96))",
              boxShadow: "0 8px 24px rgba(77,124,15,0.08)",
            }}
          >
            <CardContent sx={{ px: 3, py: 2.5 }}>
              <Typography
                variant="overline"
                sx={{ color: "#4d7c0f", fontWeight: 700, letterSpacing: 1 }}
              >
                Logged in as
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 700 }}>
                {loggedInAs}
              </Typography>
              {userHandle && (
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                  {userHandle}
                </Typography>
              )}
              <Button
                fullWidth
                variant="outlined"
                color="success"
                onClick={handleLogout}
                sx={{ mt: 2, fontWeight: 700 }}
              >
                Log Out
              </Button>
            </CardContent>
          </Card>

          <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 1,
              gap: 1,
            }}
          >
            <DashboardCustomizeIcon sx={{ fontSize: 32, color: "#4d7c0f" }} />
            <Typography variant="h4" fontWeight={700}>
              Mobile Queue
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{ color: "text.secondary", mt: 1, maxWidth: 560, mx: "auto" }}
          >
            Choose a sublocation to open its Mobile Queue Screen.
          </Typography>

          <Divider
            sx={{
              mt: 3,
              width: 80,
              mx: "auto",
              borderColor: "#65a30d",
            }}
          />
          </Box>
        </Box>

        {visibleSublocations.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 3,
              textAlign: "center",
              borderRadius: 3,
              border: "1px dashed rgba(0,0,0,0.12)",
              backgroundColor: "rgba(255,255,255,0.7)",
            }}
          >
            <Typography variant="body1" fontWeight={500}>
              {hasScopeRoles
                ? "No mobile queue destinations found for your assigned scope."
                : "No screen scope roles assigned."}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              {hasScopeRoles
                ? "If this seems wrong, check your site/location/sublocation permissions."
                : "Assign one of: screens:all, screens:site:<id>, screens:location:<id>, screens:sublocation:<id>."}
            </Typography>
          </Box>
        ) : (
          Object.values(groupedSublocations).map((siteObj) => (
            <Box key={`site:${siteObj.siteId ?? "unassigned"}`} sx={{ mb: 4 }}>
              {Object.values(siteObj.locations).map((locObj) => (
                <Box key={`loc:${locObj.locationId ?? "unassigned"}`} sx={{ mb: 3 }}>
                  <ScreenRow
                    items={locObj.items}
                    renderItem={(row) => (
                      <ScreenCard
                        label={row.sublocationName}
                        hierarchyLabel={null}
                        onClick={() => navigate(mobileQueueRouteFor(row.sublocationId))}
                      />
                    )}
                  />
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
